// Stylesheet entry — bundled by Vite into www/styles.bundle.css.
// Loaded BEFORE any JS modules so the first paint already has the new design.
import '../styles/index.css';

import { createLogger } from '../core/logger.js';
import * as eventBus from '../core/eventBus.js';
import { appStore, Store } from '../core/store.js';
import { LocalState } from '../core/localState.js';
import * as firebaseCore from '../core/firebase.js';
import * as spinner from '../core/spinner.js';
import * as modal from '../core/modal.js';
import * as alert from '../core/alert.js';

import * as escapeUtil from '../shared/escape.js';
import * as plate from '../shared/plate.js';
import * as phone from '../shared/phone.js';
import * as date from '../shared/date.js';
import * as geo from '../shared/geo.js';
import * as timing from '../shared/timing.js';
import * as promiseUtil from '../shared/promise.js';
import * as dom from '../shared/dom.js';

import * as config from './config.js';
import * as ckan from '../data/apis/ckan.js';
import * as locationiq from '../data/apis/locationiq.js';
import * as usersRepo from '../data/repositories/users.repo.js';
import * as vehiclesRepo from '../data/repositories/vehicles.repo.js';
import * as indexesRepo from '../data/repositories/indexes.repo.js';

import * as authFeature from '../features/auth/index.js';
import * as vehicleFeature from '../features/vehicle/index.js';
import * as messagingFeature from '../features/messaging/index.js';
import * as callingFeature from '../features/calling/index.js';
import * as shareFeature from '../features/share/index.js';
import * as autoServicesFeature from '../features/autoServices/index.js';
import * as notificationsFeature from '../features/notifications/index.js';
import * as adsFeature from '../features/ads/index.js';
import * as homeFeature from '../features/home/index.js';
import * as phantomFeature from '../features/phantom/index.js';
import * as pwaFeature from '../features/pwa/index.js';
import * as settingsFeature from '../features/settings/index.js';
import * as infoFeedFeature from '../features/infoFeed/index.js';
import * as aiFeature from '../features/ai/index.js';
import * as dnaView from '../features/vehicle/dna.view.js';
import * as fineAppealFeature from '../features/fineAppeal/index.js';
import * as photoDiagFeature from '../features/photoDiagnostic/index.js';
import * as disabledBadgeFeature from '../features/disabledBadge/index.js';
import * as usedCarCheckFeature from '../features/usedCarCheck/index.js';
import * as testReminderFeature from '../features/testReminder/index.js';
import * as adminFeature from '../features/admin/index.js';
import * as adminService from '../features/auth/admin.service.js';
import { getUserLocation as getUserLocationNative } from '../core/geolocation.js';

// Attach legacy compatibility globals (window.escapeHtml etc.). Must run
// before any legacy script that depends on those globals — index.html loads
// the bundle first.
import './legacy-shims.js';

const log = createLogger('bootstrap');

/**
 * Keeps the native AdMob banner in sync with the visibility of the
 * splash `#loadingScreen`. The banner is a native View overlaid on top
 * of the WebView, so HTML z-index can't cover it — we have to call
 * `hideBanner` / `showBanner` based on whether the splash is present.
 *
 * Lifecycle handled:
 *   - Initial boot:  splash visible → hide. Splash removes → show.
 *   - Any logout:    splash re-mounts → hide. Splash removes again → show.
 *
 * Idempotent — only the FIRST transition fires (it compares to `last`),
 * so we don't spam the AdMob plugin with redundant calls.
 */
function watchSplashForBanner() {
  /** @type {boolean | null} */
  let last = null;
  const getIsGone = () => {
    const el = document.getElementById('loadingScreen');
    return !el || el.classList.contains('is-leaving');
  };
  const apply = () => {
    const isGone = getIsGone();
    if (isGone === last) return;
    last = isGone;
    if (isGone) void adsFeature.admob.showBanner();
    else void adsFeature.admob.hideBanner();
  };
  apply();
  const observer = new MutationObserver(apply);
  observer.observe(document.body, {
    childList: true, subtree: true,
    attributes: true, attributeFilter: ['class'],
  });
}

const App = {
  ready: true,
  logger: createLogger,
  bus: eventBus,
  store: appStore,
  Store,
  localState: LocalState,
  firebase: firebaseCore,
  spinner,
  modal,
  alert,
  config,
  shared: {
    escape: escapeUtil,
    plate,
    phone,
    date,
    geo,
    timing,
    promise: promiseUtil,
    dom,
  },
  data: {
    ckan,
    locationiq,
    users: usersRepo,
    vehicles: vehiclesRepo,
    indexes: indexesRepo,
  },
  features: {
    auth: authFeature,
    vehicle: vehicleFeature,
    messaging: messagingFeature,
    calling: callingFeature,
    share: shareFeature,
    autoServices: autoServicesFeature,
    notifications: notificationsFeature,
    ads: adsFeature,
    home: homeFeature,
    phantom: phantomFeature,
    admin: adminFeature,
  },
};

// @ts-ignore - intentional global for legacy interop during migration
window.App = App;

// Expose the Variant-B home view so `www/modals/home-feed.js::initHomeFeed`
// can delegate to it. Kept on a namespaced global to avoid polluting `window`.
/** @type {any} */
(window).AutoMe = /** @type {any} */ (window).AutoMe || {};
/** @type {any} */
(window).AutoMe.homeView = homeFeature.view;

// AutoMe AI chat — opened from the floating action button, the daily
// brief contextual chip, and possibly other entry points. `view.open()`
// is idempotent: re-clicking simply re-focuses the existing modal.
// The 2nd argument is for advanced openers (e.g. Photo Diagnostic intro
// passes `{ autoTriggerPhoto: true }` to skip welcome & fire the file picker).
/** @type {any} */
(window).openAiChat = (preset, opts) => aiFeature.view.open(preset, opts);

// 🧬 Vehicle DNA — opened from the My Car screen or future entry points.
/** @type {any} */
(window).openVehicleDNA = () => dnaView.open();

// 🎫 Fine Appeal — opened from the home grid or My Car. Multi-step flow.
/** @type {any} */
(window).openFineAppeal = () => fineAppealFeature.view.open();

// 🔬 Photo Diagnostic — opens the educational intro modal. The modal's
// CTA then opens the AI chat with the photo picker auto-triggered.
/** @type {any} */
(window).openPhotoDiagnostic = () => photoDiagFeature.intro.open();

// ♿ Disabled-badge check — exposes the modal opener as a global so future
// callers (small grid card, deep links, etc) can trigger it. The home-screen
// hero card calls `disabledBadgeFeature.openModal` directly via its own
// inline submit handler, so this global isn't strictly needed today, but
// it keeps the entry-point API consistent with the other features above.
/** @type {any} */
(window).openDisabledBadgeCheck = (/** @type {string=} */ plate) => {
  if (plate) return disabledBadgeFeature.openModal(plate);
  // No plate provided — scroll the home card into view so the user
  // can type one. Idempotent: if the card isn't on screen (e.g. user
  // is on a different tab), just no-op.
  const card = document.getElementById('badgeCheckCard');
  if (card) {
    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    /** @type {HTMLInputElement | null} */
    (card.querySelector('#badgeCheckInput'))?.focus();
  }
};

// 🔍 Used-car-check — same dual-mode API as the badge check: pass a plate
// to jump straight to the report modal, or call with no args to scroll
// the home-screen card into view and focus its input.
/** @type {any} */
(window).openUsedCarCheck = (/** @type {string=} */ plate) => {
  if (plate) return usedCarCheckFeature.openModal(plate);
  const card = document.getElementById('usedCarCheckCard');
  if (card) {
    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    /** @type {HTMLInputElement | null} */
    (card.querySelector('#usedCarInput'))?.focus();
  }
};

// 🗓️ Test-reminder modal — opened by the test-expiry push notification
// (the push's `click_action` deep-links here). Can also be called manually
// without args — falls back to the signed-in user's vehicle.
/** @type {any} */
(window).openTestReminder = (/** @type {string=} */ plate) => testReminderFeature.open(plate);

// Mount the global floating action button. Idempotent — safe to call
// before DOMContentLoaded; the module schedules itself if needed.
aiFeature.fab.mount();

// Admin banner — visible only when the signed-in user's phone matches
// `/config/admins/phones`. Mounts a compact stats strip below the home
// header and an "⚙️ פאנל אדמין" button that opens the admin page in a
// full-screen iframe modal (no extra password — the iframe uses the
// shared Firebase auth session via `?fromApp=1`).
async function syncAdminBanner() {
  // Top-level console.log so we can see this fire even if the logger
  // module isn't behaving for some reason (cache, filter, log-level set).
  // eslint-disable-next-line no-console
  console.log('[admin-banner] syncAdminBanner() called', {
    phone: LocalState.phone,
    hasMount: typeof adminFeature?.banner?.mount === 'function',
  });
  try {
    log.info('admin-banner: sync starting…');
    // adminService.ensureLoaded() subscribes to /config/admins via
    // `.on('value')`. The RTDB rule requires `auth != null` — without it
    // the read is buffered indefinitely (no success, no error). So we
    // wait for BOTH the SDK link AND an authenticated user before
    // calling ensureLoaded, otherwise the immediate-fire trigger hangs
    // forever and never reports admin status.
    await firebaseCore.init();
    await firebaseCore.ensureAuthReady();
    await adminService.ensureLoaded();
    const phone = LocalState.phone || '';
    const isAdmin = adminService.isAdminPhone(phone);
    // eslint-disable-next-line no-console
    console.log('[admin-banner] decision:', { phone, isAdmin });
    if (isAdmin) {
      // The /adminUsers/{uid} self-register that the RTDB rules require
      // for the stats reads is handled inside adminStats.service.js
      // (`ensureAdminRegistered`), called before every fetch.
      adminFeature.banner.mount();
    } else {
      adminFeature.banner.unmount();
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[admin-banner] sync threw:', e);
    log.warn('admin banner sync failed:', e);
  }
}
// Expose for manual testing from the dev console.
/** @type {any} */ (window).__syncAdminBanner = syncAdminBanner;

// Aggressive multi-trigger sync — relying on a single event (auth:ready)
// proved unreliable in some bootstrap orderings, so we now fire the
// admin-banner sync from FIVE different triggers. `syncAdminBanner` is
// idempotent (mount/unmount checks are no-ops if the state matches) so
// duplicate calls are harmless.
eventBus.on('auth:ready', () => { void syncAdminBanner(); });
eventBus.on('auth:logged-out', () => { adminFeature.banner.unmount(); });

// ----------------------------------------------------------------------------
// Search-bypass flag — live mirror from `users/{uid}/searchBypass` into
// LocalState. The admin panel writes that field to grant a specific user
// the ability to search without registering a vehicle (journalists, support
// staff, partners). The searchGate reads `LocalState.searchBypass` so admin
// toggles take effect IMMEDIATELY — no reload required.
//
// We attach the listener on every auth:ready and detach on auth:logged-out
// to avoid stale subscriptions across user switches.
// ----------------------------------------------------------------------------
/** @type {{ ref: any; off: () => void } | null} */
let searchBypassWatcher = null;
eventBus.on('auth:ready', (/** @type {any} */ payload) => {
  const uid = payload?.uid;
  if (!uid) return;
  // Detach any previous subscription first — guards against multiple
  // auth:ready firings for the same user (cold start + validateSession).
  if (searchBypassWatcher) {
    try { searchBypassWatcher.off(); } catch (_) { /* ignore */ }
    searchBypassWatcher = null;
  }
  const bypassRef = firebaseCore.ref(`users/${uid}/searchBypass`);
  const handler = (/** @type {any} */ snap) => {
    LocalState.searchBypass = snap.val() === true;
    log.info('searchBypass synced:', LocalState.searchBypass);
  };
  bypassRef.on('value', handler);
  searchBypassWatcher = { ref: bypassRef, off: () => bypassRef.off('value', handler) };
});
eventBus.on('auth:logged-out', () => {
  if (searchBypassWatcher) {
    try { searchBypassWatcher.off(); } catch (_) { /* ignore */ }
    searchBypassWatcher = null;
  }
  LocalState.searchBypass = false;
});

// 1) Immediately — covers the case where the user is already logged in
//    and LocalState is populated by the time the bundle finishes parsing.
void syncAdminBanner();

// 2) When the DOM is ready — needed because `mount()` requires
//    `#homeScreen .home-header`, which is parsed after the bundle.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => { void syncAdminBanner(); }, { once: true });
} else {
  setTimeout(() => { void syncAdminBanner(); }, 0);
}

// 3) After 2.5 seconds — covers the validateSession path where auth:ready
//    fires asynchronously after server roundtrips.
setTimeout(() => {
  // eslint-disable-next-line no-console
  console.log('[admin-banner] 2.5s fallback firing');
  void syncAdminBanner();
}, 2500);

// 4) After 6 seconds — last resort if the home screen takes time to mount.
setTimeout(() => {
  // eslint-disable-next-line no-console
  console.log('[admin-banner] 6s last-resort firing');
  void syncAdminBanner();
}, 6000);

// Tag the body so CSS can show/hide UI per platform. `is-native` = Capacitor
// app on Android/iOS, `is-web` = regular browser. Used to hide the
// plate-scanner camera button on the web (ML Kit native works perfectly but
// Tesseract.js in the browser is too unreliable to expose — better to leave
// users with manual entry only).
(() => {
  const cap = /** @type {any} */ (window).Capacitor;
  const native = !!cap?.isNativePlatform?.();
  document.body.classList.toggle('is-native', native);
  document.body.classList.toggle('is-web', !native);
})();

// Replace the legacy `getUserLocation` (which uses `navigator.geolocation` —
// flaky inside Capacitor's WebView) with one that uses the native Geolocation
// plugin on Android/iOS and falls back to the browser API on the web.
// MUST run AFTER `www/modals/geolocation.js` has loaded, otherwise its plain
// function declaration overwrites our `window.getUserLocation` again. Legacy
// scripts load synchronously after this bundle, so a `DOMContentLoaded`
// listener is the right hook.
function installGeolocationOverride() {
  const w = /** @type {any} */ (window);
  w.getUserLocation = (/** @type {any} */ options) => getUserLocationNative(options || {});
  // Also reset any pending `__locInitPromise` from the legacy auto-init so a
  // fresh request actually fires through our implementation.
  if (w.__locInitPromise && !w.__userLoc) delete w.__locInitPromise;
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', installGeolocationOverride, { once: true });
} else {
  installGeolocationOverride();
}

log.info('phase B.1 ready — auth feature attached');

// Bind the login form when DOM is ready (no auth required for this).
authFeature.loginView.init();

// Wire interactive 3D-tilt + glare + live-dot effects to home-feed cards.
// Idempotent + DOM-observer based, safe to run before #homeFeed exists.
homeFeature.cardEffects.init();

// Wire the home-screen profile button to open the account drawer, and run
// `initAccountDrawerIcon()` so the drawer's nav tabs (my car / history /
// messages / about) get their click handlers. The legacy `fcm-notifications.js`
// used to call `initAccountDrawerIcon` on DOMContentLoaded — that file was
// removed in phase B.3, so without this the tabs stay inert and clicking them
// looks like the drawer is "frozen".
function wireHomeDrawerHooks() {
  const w = /** @type {any} */ (window);

  const btn = document.getElementById('homeProfileBtn');
  if (btn && btn.dataset.bound !== '1') {
    btn.dataset.bound = '1';
    btn.addEventListener('click', () => {
      // Priority: unread messages > license/ownership warning > regular drawer.
      // The drawer is the same in all three cases — we just open it on a
      // different tab so the user lands directly on the relevant content
      // while still being able to navigate between tabs.
      const badge = document.getElementById('headerNotificationBadge');
      const hasUnread = !!(badge && badge.textContent && badge.textContent.trim());
      if (hasUnread && typeof w.openAccountDrawer === 'function') {
        w.openAccountDrawer('notifications');
        return;
      }
      const hasAlert = !!btn.querySelector('.license-alert-dot');
      if (hasAlert && typeof w.openAccountDrawer === 'function') {
        // Wait for the mycar tab's banners to actually render (renderDrawerTab
        // awaits the gov-bundle fetch + banner render), then pulse the
        // offending one. This makes the animation fire reliably on EVERY
        // click, not just the first — earlier versions polled after a fixed
        // delay and would race with the innerHTML wipe of accountContent.
        Promise.resolve(w.openAccountDrawer('mycar'))
          .then(() => highlightAlertBanner())
          .catch((e) => log.warn('openAccountDrawer mycar failed:', e));
        return;
      }
      if (typeof w.openAccountDrawer === 'function') w.openAccountDrawer();
      else log.warn('openAccountDrawer not available yet');
    });
  }

  if (typeof w.initAccountDrawerIcon === 'function') {
    try { w.initAccountDrawerIcon(); }
    catch (e) { log.warn('initAccountDrawerIcon failed:', e); }
  }

  // The bell tab (`data-tab="notifications"`) is now handled by the unified
  // `renderDrawerTab` — it renders the inbox list directly into
  // #accountContent via `messaging.inboxView.renderInto`. No separate
  // interceptor needed here.
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', wireHomeDrawerHooks, { once: true });
} else {
  wireHomeDrawerHooks();
}

// On a successful vehicle replacement, refresh the My-Car drawer / sub-modal
// banners. `replaceVehicle.view` already calls `renderSection()` after the
// modal closes, but only for the entry path through the modal — wiring it
// to the bus covers every entry point in one place.
eventBus.on('vehicle:changed', () => {
  vehicleFeature.myCarView.renderSection().catch((e) =>
    log.warn('renderSection after vehicle:changed failed:', e),
  );
});

// Refresh the home-page "My Car" card whenever the unified alert state
// changes. `alertStatus.refresh` writes the new state to localStorage and
// THEN emits `license:status`, so by the time this listener fires the
// `card_myCar` reader will see the fresh `c2c_alert_state_${plate}` cache.
//
// We listen to `license:status` rather than `vehicle:changed` for this
// because `alertStatus.refresh` is async — listening to `vehicle:changed`
// directly would race with it and re-render the card while the cache is
// still stale.
eventBus.on('license:status', () => {
  const w = /** @type {any} */ (window);
  if (typeof w.initHomeFeed === 'function') {
    try { w.initHomeFeed(); }
    catch (e) { log.warn('initHomeFeed after license:status failed:', e); }
  }
});

// Pulse the banner(s) that triggered the warning triangle. The mycar tab
// renders banners asynchronously inside #accountContent (and the gov-API
// fetch chained before that can take many seconds on slow networks), so we
// can't rely on a fixed polling window. Instead, watch the subtree with a
// MutationObserver and fire the pulse the moment a matching banner appears.
const ALERT_BANNER_SELECTOR =
  '.license-banner.license-red, .license-banner.license-orange, ' +
  '.ownership-banner[data-status="mismatch"], .ownership-banner[data-status="partial"]';

/** @type {number | null} */
let __bannerAttentionCleanupId = null;

function highlightAlertBanner() {
  const host = document.getElementById('accountContent');
  if (!host) return;

  /** @param {HTMLElement[]} targets */
  const fire = (targets) => {
    // Cancel any prior cleanup so back-to-back clicks don't strip the class
    // from a freshly-started animation.
    if (__bannerAttentionCleanupId !== null) {
      clearTimeout(__bannerAttentionCleanupId);
      __bannerAttentionCleanupId = null;
    }
    targets.forEach((el) => {
      el.classList.remove('banner-attention');
      void el.offsetWidth; // restart animation
      el.classList.add('banner-attention');
    });
    targets[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
    __bannerAttentionCleanupId = /** @type {any} */ (setTimeout(() => {
      targets.forEach((el) => el.classList.remove('banner-attention'));
      __bannerAttentionCleanupId = null;
    }, 5000));
  };

  // Already there? Fire immediately.
  const immediate = /** @type {HTMLElement[]} */ (
    Array.from(host.querySelectorAll(ALERT_BANNER_SELECTOR))
  );
  if (immediate.length) { fire(immediate); return; }

  // Otherwise watch for them. Give up after 15s.
  /** @type {MutationObserver | null} */
  let obs = new MutationObserver(() => {
    const targets = /** @type {HTMLElement[]} */ (
      Array.from(host.querySelectorAll(ALERT_BANNER_SELECTOR))
    );
    if (targets.length) {
      obs?.disconnect();
      obs = null;
      // One frame delay so any concurrent inline-style write in `setBanner`
      // settles before we add our class.
      requestAnimationFrame(() => fire(targets));
    }
  });
  obs.observe(host, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'data-status'] });
  setTimeout(() => { obs?.disconnect(); obs = null; }, 15000);
}

// Wait for the Firebase compat SDK loaded by index.html, then bootstrap auth:
// decide between login / home screen and validate session against the server.
/** @type {{ claimed: boolean; phone?: string; plate?: string; year?: string }} */
let __installBridge = { claimed: false };

/**
 * Tiny on-device debug logger that writes bootstrap milestones to RTDB so
 * we can diagnose iPhone PWA issues without a Mac+Safari Web Inspector.
 * Reads (admin-only) at `pwaDebug/{uid}/...`.
 * Best-effort — failures are silenced.
 *
 * @param {string} event
 * @param {object} [data]
 */
function pwaDebug(event, data = {}) {
  try {
    const uid = firebaseCore.auth().currentUser?.uid;
    if (!uid) return;
    const key = `${Date.now()}_${event}`;
    firebaseCore.ref(`pwaDebug/${uid}/${key}`).set({ event, ...data });
  } catch (e) { /* ignore */ }
}

// Immediate native-banner cleanup. The AdMob banner is a native View that
// survives `window.location.reload()` (e.g. on logout) — so on a fresh
// WebView boot the banner can sit on top of the splash before any module
// inside the new WebView has a chance to react. Call hideBanner directly
// on the plugin object as soon as bootstrap loads, without waiting for
// `admob.init`. Safe to call before init — the Capacitor plugin no-ops or
// errors silently if the native side isn't initialized yet.
try {
  const W = /** @type {any} */ (window);
  const AdMob = W.Capacitor?.Plugins?.AdMob;
  if (AdMob && typeof AdMob.hideBanner === 'function') {
    Promise.resolve(AdMob.hideBanner()).catch(() => {});
  }
} catch { /* ignore */ }

firebaseCore.init()
  .then(() => firebaseCore.ensureAuthReady())
  // PWA install-session bridge — if the URL has an `install_token` query
  // param (set by `installPrompt.view.js` on the Safari side just before
  // the user did "Add to Home Screen"), claim it now so LocalState is
  // seeded with the user's phone/plate/year BEFORE validateSession runs.
  // The Cloud Function also writes `trustedDevices/{phone}/{deviceId}` so
  // the subsequent login attempt skips OTP. Non-fatal — if claim fails the
  // user just sees the normal login form.
  .then(async () => {
    pwaDebug('bootstrap-start', {
      url: window.location.href,
      ua: navigator.userAgent.slice(0, 200),
      standalone: !!(/** @type {any} */(navigator).standalone),
      lsKeys: (() => {
        try { return Object.keys(localStorage); } catch { return []; }
      })(),
    });
    try { __installBridge = await pwaFeature.sessionBridge.claim(); }
    catch (e) { log.warn('install-session claim failed:', e); }
    pwaDebug('claim-result', {
      claimed: __installBridge.claimed,
      phone: __installBridge.phone || null,
      plate: __installBridge.plate || null,
      year: __installBridge.year || null,
    });
  })
  .then(() => {
    // Load admin config (privileged phone/uid lists) in the background.
    authFeature.admin.ensureLoaded().catch((e) => log.warn('admin config load:', e));
    // User preferences (theme, notifications, avatar, quiet hours) —
    // applies the saved theme synchronously and starts the RTDB sync.
    settingsFeature.service.init();
    // Expose the settings modal globally so the legacy account-drawer
    // "אודות" tab can open it without an extra import.
    /** @type {any} */ (window).openSettingsModal = settingsFeature.view.open;
    // Inline renderer for the account-drawer "הגדרות" tab. Same content
    // as the modal but rendered directly into the tab's host element.
    /** @type {any} */ (window).renderSettingsInline = settingsFeature.view.renderInto;
    // (Info-feed cards are rendered INSIDE homeView now — see
    //  src/features/home/homeView.js. The standalone mountAll() call is
    //  no longer needed.)
    // Alert dot updates on vehicle/license events.
    vehicleFeature.alertStatus.init();
    // Real-time watcher on `vehicles/{plate}/ownershipMismatch` — flips
    // the search-block + repaints banners the moment the admin toggles
    // the DEV-tool flag (or a future server-side trigger sets it).
    vehicleFeature.ownershipWatcher.init();
    // Unread-message badge — listens to inbox + paints #headerNotificationBadge
    // and pushes the count to the Android launcher badge.
    messagingFeature.unreadBadge.init();
    // Phantom-vehicle feature — listens to `config/phantomEnabled` in RTDB
    // so the admin can toggle it live without redeploying. Default is "on".
    phantomFeature.config.init();
    // Push notifications — facade picks native (Capacitor) or web (FCM browser)
    // automatically. The `onMessage` hook refreshes the inbox so the unread
    // badge updates in real time when a foreground push arrives.
    void notificationsFeature.push.init({
      onMessage: (notif) => {
        log.info('push received:', notif?.data?.convId ?? notif?.data?.type ?? 'unknown');
        try { messagingFeature.conversation.list(); }
        catch (e) { log.warn('inbox refresh after push failed:', e); }

        // Tap on a license/test-reminder push → open the unified test
        // reminder modal (testime book + clipboard + pay gov.il fee).
        // `tapped=true` means user clicked the notification (background
        // tap); we don't fire when the push arrives in foreground because
        // it would interrupt whatever the user was doing.
        if (notif?.tapped && notif?.data?.type === 'test_reminder') {
          const plate = /** @type {string | undefined} */ (notif.data.plate);
          try { testReminderFeature.open(plate); }
          catch (e) { log.warn('testReminder.open failed after push tap:', e); }
        }
      },
    });
    // iOS PWA: `Notification.requestPermission()` silently no-ops outside
    // a user gesture, so push.init can't request permission on bootstrap.
    // This banner appears for standalone PWAs that haven't decided yet —
    // tapping "אפשר" runs requestPermissionAndRegister from a real click.
    notificationsFeature.permissionBanner.init();
    // AdMob (native) — initialise + pre-load interstitial / rewarded so
    // the first show() call returns instantly. AdSense (web) — load the
    // SDK and render the static slots placed in index.html. Each call is
    // a no-op on the other platform, so it's safe to fire both.
    //
    // After init, install a persistent observer that toggles the native
    // banner based on the splash `#loadingScreen` visibility — handles
    // both initial boot AND any future logout that re-shows the splash.
    void adsFeature.admob.init().then((ok) => {
      if (!ok) return;
      watchSplashForBanner();
    });
    void adsFeature.adsense.init();
    // iOS "Add to Home Screen" prompt — only fires on iPhone Safari that
    // hasn't already installed the PWA. Skipped silently on Android, native
    // Capacitor, and on devices already running standalone.
    pwaFeature.installPrompt.init();
    return authFeature.service.bootstrapInitialScreen();
  })
  .then(async (screen) => {
    log.info('initial screen:', screen);
    pwaDebug('initial-screen', { screen, claimed: __installBridge.claimed });
    if (screen === 'login' && __installBridge.claimed) {
      const { phone, plate, year } = __installBridge;
      log.info('install-bridge → auto-login', { phone, plate, year });
      pwaDebug('auto-login-start', { phone, plate, year });
      // Visible toast so the user (and we) can see the bridge worked even
      // before the login completes.
      try {
        const alertMod = await import('../core/alert.js');
        alertMod.showToast('זוהית מהמכשיר הקודם — מתחבר אוטומטית…', { kind: 'info', duration: 6000 });
      } catch { /* ignore */ }
      try {
        const result = await authFeature.service.registerOrLogin({
          phone: phone || '',
          plate: plate || '',
          year: year || '',
          termsAccepted: true,
        });
        log.info('install-bridge auto-login result:', result.status, result.message || '');
        pwaDebug('auto-login-result', {
          status: result.status,
          message: result.message || null,
        });
        if (result.status === 'logged-in') {
          const w = /** @type {any} */ (window);
          if (typeof w.showHomeScreen === 'function') w.showHomeScreen();
        } else {
          // Surface the reason so the user understands why auto-login
          // didn't complete (gov-year mismatch, blocked phone, etc.).
          try {
            const alertMod = await import('../core/alert.js');
            alertMod.showToast(
              `התחברות אוטומטית נכשלה: ${result.message || result.status}. יש להתחבר ידנית.`,
              { kind: 'warn', duration: 6000 },
            );
          } catch { /* ignore */ }
        }
      } catch (err) {
        log.error('install-bridge auto-login threw:', err);
      }
    }
  })
  .catch((err) => log.warn('bootstrap auth chain:', err));
